import React, { useState, useEffect } from 'react';
import { X, DollarSign, Check, Loader2, Percent, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const AssignCollegeModal = ({ student, colleges, onClose, onSaved }) => {
  const { toast } = useToast();
  const [collegeId, setCollegeId] = useState(student.assigned_college || '');
  const [programId, setProgramId] = useState(student.assigned_program_id || '');
  // Commission editor: mode toggle + value. Pre-fill from existing snapshot.
  const [commMode, setCommMode] = useState('flat'); // 'flat' | 'percent'
  const [commValue, setCommValue] = useState(student.recruiter_commission_snapshot || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const college = colleges.find((c) => c.id === collegeId);
  const collegeFee = Number(college?.college_fee || 0);

  const computedCommission = commMode === 'percent'
    ? Math.round((collegeFee * (parseFloat(commValue) || 0) / 100) * 100) / 100
    : (parseFloat(commValue) || 0);

  const hccRevenue = collegeFee - computedCommission;

  const submit = async (e) => {
    e.preventDefault();
    if (!collegeId) { toast({ title: 'Select a college' }); return; }
    setBusy(true);
    try {
      const body = {
        college_id: collegeId,
        program_id: programId || null,
        // Sale price snapshot equals the college fee — they're treated as the
        // same figure in this pricing model. Backend field kept for compatibility.
        sale_price: collegeFee,
      };
      if (commMode === 'percent') body.commission_percent = parseFloat(commValue) || 0;
      else body.commission_amount = parseFloat(commValue) || 0;
      await api.put(`/admin/students/${student.id}/college`, body);
      toast({ title: 'College assigned', description: `${student.full_name} → ${college?.name}` });
      onSaved?.();
    } catch (err) {
      toast({ title: 'Assign failed', description: err?.response?.data?.detail || 'Error' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl pointer-events-auto">
          <div className="sticky top-0 bg-white z-10 border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Assign College</div>
              <h3 className="font-display text-xl text-[#0B5E2A]">{student.full_name}</h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]"><X className="h-5 w-5 pointer-events-none" /></button>
          </div>
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">College *</label>
              <select value={collegeId} onChange={(e) => { setCollegeId(e.target.value); setProgramId(''); }} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                <option value="">Select a college...</option>
                {colleges.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.location}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Program</label>
              <select value={programId} onChange={(e) => setProgramId(e.target.value)} disabled={!college} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 disabled:bg-[#f6faf3]">
                <option value="">{college ? 'Select a program...' : 'Choose college first'}</option>
                {(college?.programs || []).map((p) => <option key={p.id} value={p.id}>{p.name} — ${Number(p.default_sale_price || 0).toLocaleString()}</option>)}
              </select>
            </div>

            <div className="rounded-lg border border-[#F0B93C]/40 bg-[#FEF8E6] p-4 space-y-3">
              <div className="flex items-center gap-2 text-[#8a5e00] text-sm font-semibold"><DollarSign className="h-4 w-4" /> Pricing (admin only)</div>

              {/* Editable recruiter commission: flat $ or % of college fee. */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-[#8a5e00]">Recruiter Commission</label>
                  <div className="inline-flex bg-white border border-[#F0B93C]/40 rounded-md overflow-hidden text-[11px] font-semibold">
                    <button type="button" onClick={() => setCommMode('flat')} data-testid="comm-mode-flat" className={`px-2.5 py-1 ${commMode === 'flat' ? 'bg-[#0B5E2A] text-white' : 'text-[#8a5e00] hover:bg-[#FEF8E6]'}`}>$ Flat</button>
                    <button type="button" onClick={() => setCommMode('percent')} data-testid="comm-mode-percent" className={`px-2.5 py-1 ${commMode === 'percent' ? 'bg-[#0B5E2A] text-white' : 'text-[#8a5e00] hover:bg-[#FEF8E6]'}`}>% of Fee</button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#8a5e00] font-semibold w-4 flex items-center justify-center">
                    {commMode === 'percent' ? <Percent className="h-3.5 w-3.5" /> : '$'}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step={commMode === 'percent' ? '0.1' : '50'}
                    max={commMode === 'percent' ? '100' : undefined}
                    value={commValue}
                    onChange={(e) => setCommValue(e.target.value)}
                    data-testid="assign-commission-input"
                    className="flex-1 px-3 py-2 rounded-md border border-[#F0B93C]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#F0B93C]/30 bg-white"
                  />
                  {commMode === 'percent' && (
                    <span className="text-xs text-[#6b7e6d] whitespace-nowrap" data-testid="assign-commission-preview">= ${Number(computedCommission).toLocaleString()}</span>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-[#8a5e00]/80">
                  {commMode === 'percent' ? 'Percentage of the college fee.' : 'Flat dollar amount paid to the recruiter for this student.'}
                </p>
              </div>

              {/* Breakdown: College Fee − Recruiter Commission = HCC Revenue */}
              <div className="rounded-lg bg-white border border-[#F0B93C]/40 overflow-hidden">
                <div className="px-3 py-2 border-b border-[#F0B93C]/20 flex items-center justify-between text-[12px]">
                  <span className="text-[#6b7e6d] uppercase tracking-wider font-semibold">College Fee</span>
                  <span className="font-semibold text-[#1b2a1f]">${collegeFee.toLocaleString()}</span>
                </div>
                <div className="px-3 py-2 border-b border-[#F0B93C]/20 flex items-center justify-between text-[12px]">
                  <span className="text-[#6b7e6d] flex items-center gap-1.5">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-[#F0B93C]/20 text-[#8a5e00] text-[10px] font-bold">−</span>
                    Recruiter Commission
                  </span>
                  <span className="font-semibold text-[#1b2a1f]">${Number(computedCommission).toLocaleString()}</span>
                </div>
                <div className={`px-3 py-2.5 flex items-center justify-between ${hccRevenue < 0 ? 'bg-[#9b1c1c] text-white' : 'bg-[#0B5E2A] text-white'}`}>
                  <span className="text-xs uppercase tracking-wider font-semibold">= HCC Revenue</span>
                  <span className="font-display text-lg font-bold" data-testid="assign-hcc-revenue">${Number(hccRevenue).toLocaleString()}</span>
                </div>
              </div>
              {hccRevenue < 0 && (
                <div className="flex items-start gap-2 rounded-md bg-[#FDE2E2] border border-[#9b1c1c]/40 px-3 py-2 text-[12px] text-[#7a1313]" data-testid="assign-negative-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">HCC loses money on this assignment.</div>
                    Recruiter commission exceeds the college fee by ${Number(Math.abs(hccRevenue)).toLocaleString()}. You can still save, but double-check the numbers.
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2 border-t border-[#0B5E2A]/10">
              <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">Cancel</button>
              <button type="submit" disabled={busy || !collegeId} className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Assigning...</>) : (<><Check className="h-4 w-4 pointer-events-none" /> Save Assignment</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AssignCollegeModal;
